import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';
import { Building2, Menu, X } from 'lucide-react';
import { useState } from 'react';

interface TenantHeaderProps {
  showNavigation?: boolean;
  className?: string;
}

export const TenantHeader: React.FC<TenantHeaderProps> = ({ 
  showNavigation = true, 
  className = '' 
}) => {
  const { tenant, loading } = useTenant();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Build navigation items with tenant-aware prefixes when applicable
  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;

  const navigationItems = [
    { name: 'Home', href: tLink('/') },
    { name: 'Products', href: tLink('/products') },
    { name: 'Decision Tree', href: tLink('/decision-tree') },
    { name: 'The Chronicle', href: tLink('/chronicle') },
    { name: 'About Us', href: tLink('/about-us') },
    { name: 'Contact', href: '#contact' },
  ];

  const handleLogoClick = () => {
    navigate(tLink('/'));
    setMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <header className={`bg-white shadow-sm border-b ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={`tenant-nav shadow-sm border-b sticky top-0 z-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Navigation */}
        <div className="hidden sm:flex items-center justify-between py-4">
          {/* Logo Section */}
          <div
            className="flex items-center space-x-3 cursor-pointer"
            onClick={handleLogoClick}
          >
            {tenant?.logoUrl ? (
              <img
                src={tenant.logoUrl}
                alt={`${tenant.name} logo`}
                className="tenant-logo h-10 w-auto"
                onError={(e) => {
                  // Fallback to default logo or icon if tenant logo fails to load
                  e.currentTarget.style.display = 'none';
                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.classList.remove('hidden');
                  }
                }}
              />
            ) : (
              <Building2 className="h-8 w-8 tenant-primary-text" />
            )}
            
            {/* Fallback icon (hidden by default, shown if logo fails) */}
            {tenant?.logoUrl && (
              <Building2 className="h-8 w-8 tenant-primary-text hidden" />
            )}
            
            <div className="flex flex-col">
              <span className="text-lg font-bold tenant-text">
                {tenant?.name || 'YallaCompare Business'}
              </span>
              <span className="text-xs text-gray-500">
                Insurance Solutions
              </span>
            </div>
          </div>

          {/* Navigation Menu */}
          {showNavigation && (
            <nav className="flex items-center space-x-8">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="tenant-nav-link text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Tenant Dashboard Link (if tenant exists) */}
              {tenant && (
                <Link
                  to="/tenant-dashboard"
                  className="tenant-nav-link text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Dashboard
                </Link>
              )}
            </nav>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="sm:hidden">
          <div className="flex items-center justify-between py-4">
            {/* Mobile Logo */}
            <div 
              className="flex items-center space-x-2 cursor-pointer" 
              onClick={handleLogoClick}
            >
              {tenant?.logoUrl ? (
                <img
                  src={tenant.logoUrl}
                  alt={`${tenant.name} logo`}
                  className="tenant-logo-small h-8 w-auto"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : (
                <Building2 className="h-6 w-6 tenant-primary-text" />
              )}
              
              {/* Fallback icon for mobile */}
              {tenant?.logoUrl && (
                <Building2 className="h-6 w-6 tenant-primary-text hidden" />
              )}
              
              <span className="text-lg font-bold tenant-text">
                {tenant?.name || 'YallaCompare'}
              </span>
            </div>

            {/* Mobile Menu Button */}
            {showNavigation && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            )}
          </div>

          {/* Mobile Menu */}
          {showNavigation && mobileMenuOpen && (
            <div className="border-t border-gray-200 py-4">
              <nav className="flex flex-col space-y-4">
                {navigationItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="tenant-nav-link text-gray-700 hover:text-blue-600 font-medium transition-colors px-2 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                
                {/* Mobile Tenant Dashboard Link */}
                {tenant && (
                  <Link
                    to="/tenant-dashboard"
                    className="tenant-nav-link text-gray-700 hover:text-blue-600 font-medium transition-colors px-2 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                )}
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};